import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Select, Space, Popconfirm, message, Tag } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { makeGetCall, makeDeleteCall } from '../../utils/helper';
import EditPricing from './EditPricing';
import './pricing-list.css';

const { Option } = Select;

const PricingList = () => {
  const [pricingList, setPricingList] = useState({ data: [], loading: false, error: null });
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState(null);

  // Filter states
  const [centreFilter, setCentreFilter] = useState(null);
  const [modalityFilter, setModalityFilter] = useState(null);

  // Dropdown data
  const [centres, setCentres] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);

  // Fetch dropdown data on mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const centresRes = await makeGetCall('/centres?active=true');
        if (centresRes.data.success) {
          setCentres(centresRes.data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch centres:', err);
      }

      try {
        const modalitiesRes = await makeGetCall('/modalities');
        if (modalitiesRes.data.success) {
          setModalities(modalitiesRes.data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch modalities:', err);
      }

      try {
        const bodyPartsRes = await makeGetCall('/body-parts');
        if (bodyPartsRes.data.success) {
          setBodyParts(bodyPartsRes.data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch body parts:', err);
      }
    };

    fetchDropdowns();
  }, []);

  // Fetch pricing list
  const fetchPricing = useCallback(async () => {
    setPricingList((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const params = new URLSearchParams();
      if (centreFilter) {
        params.append('centre_code', centreFilter);
      }
      if (modalityFilter) {
        params.append('modality', modalityFilter);
      }

      const url = `/centre-pricing${params.toString() ? '?' + params.toString() : ''}`;
      const response = await makeGetCall(url);

      if (response.data.success) {
        setPricingList({
          data: response.data.data || [],
          loading: false,
          error: null,
        });
      } else {
        throw new Error(response.data.message || 'Failed to fetch pricing rules');
      }
    } catch (error) {
      setPricingList({
        data: [],
        loading: false,
        error: error.message || 'Failed to fetch pricing rules',
      });
      message.error('Failed to fetch pricing rules');
    }
  }, [centreFilter, modalityFilter]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const handleEdit = (record) => {
    setSelectedPricing(record);
    setEditModalVisible(true);
  };

  const handleDelete = async (record) => {
    setDeleteInProgress(true);
    try {
      await makeDeleteCall(`/centre-pricing/${record.cp_id}`);
      message.success('Pricing rule deleted successfully');
      fetchPricing();
    } catch (error) {
      message.error(error.message || 'Failed to delete pricing rule');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleUpdateSuccess = () => {
    setEditModalVisible(false);
    setSelectedPricing(null);
    fetchPricing();
  };

  const handleClearFilters = () => {
    setCentreFilter(null);
    setModalityFilter(null);
  };

  const columns = [
    {
      title: 'Centre Code',
      dataIndex: 'cp_centre_code',
      key: 'cp_centre_code',
      sorter: (a, b) => a.cp_centre_code.localeCompare(b.cp_centre_code),
    },
    {
      title: 'Modality',
      dataIndex: 'cp_modality',
      key: 'cp_modality',
      sorter: (a, b) => a.cp_modality.localeCompare(b.cp_modality),
    },
    {
      title: 'Body Part',
      dataIndex: 'cp_body_part',
      key: 'cp_body_part',
      sorter: (a, b) => a.cp_body_part.localeCompare(b.cp_body_part),
    },
    {
      title: 'Price',
      dataIndex: 'cp_price',
      key: 'cp_price',
      render: (price) => `${parseFloat(price).toFixed(2)}`,
      sorter: (a, b) => parseFloat(a.cp_price) - parseFloat(b.cp_price),
    },
    {
      title: 'Status',
      dataIndex: 'cp_active',
      key: 'cp_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit Pricing Rule"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this pricing rule?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleteInProgress}
              title="Delete Pricing Rule"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-container pricing-list-container">
      <div className="admin-header">
        <h2>Pricing Rules</h2>
      </div>

      <div className="admin-filters">
        <Space size="middle" wrap>
          <Select
            placeholder="Filter by Centre"
            value={centreFilter}
            onChange={setCentreFilter}
            style={{ width: 200 }}
            allowClear
          >
            {centres.map((c) => (
              <Option key={c.code} value={c.code}>
                {c.name} ({c.code})
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Filter by Modality"
            value={modalityFilter}
            onChange={setModalityFilter}
            style={{ width: 150 }}
            allowClear
          >
            {modalities.map((m) => (
              <Option key={m.value} value={m.value}>
                {m.label}
              </Option>
            ))}
          </Select>

          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </Space>
      </div>

      {pricingList.error && (
        <div className="error-message">
          <p>{pricingList.error}</p>
          <Button onClick={fetchPricing}>Retry</Button>
        </div>
      )}

      <div className="admin-table-container">
        <Table
          columns={columns}
          dataSource={pricingList.data}
          loading={pricingList.loading}
          rowKey="cp_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} rules`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      {editModalVisible && selectedPricing && (
        <EditPricing
          visible={editModalVisible}
          pricing={selectedPricing}
          centres={centres}
          modalities={modalities}
          bodyParts={bodyParts}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedPricing(null);
          }}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
};

export default PricingList;
